import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { 
  UserCheck, 
  Clock, 
  MessageSquare, 
  FileText, 
  Shield, 
  CheckCircle,
  Heart,
  Star,
  ArrowRight,
  Zap,
  Target
} from 'lucide-react'
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics'

const Secretariado = () => {
  const { trackButtonClick } = useGoogleAnalytics();

  const handleDemoClick = () => {
    trackButtonClick('marcar_demonstracao', 'secretariado');
    window.open('https://calendly.com/cirurgiaautorizada-dev/30min', '_blank');
  };

  const handleConsultantClick = () => {
    trackButtonClick('falar_especialista', 'secretariado');
    window.open('https://wa.me/5585982286045', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-green-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <UserCheck className="h-12 w-12 text-green-300 mr-4" />
                <span className="text-green-300 text-lg font-medium">Para Secretariados Médicos</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                Sua Rotina{' '}
                <span className="text-orange-400">Simplificada</span>: Liberte-se da Burocracia e Foque no que Importa
              </h1>
              <p className="text-xl text-green-100 mb-8 leading-relaxed">
                Com a Cirurgia Autorizada, você transforma o estresse administrativo em produtividade e reconhecimento.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg"
                  onClick={handleDemoClick}
                >
                  Marcar Demonstração
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-green-900 hover:bg-gray-200 hover:text-green-900 px-8 py-4 text-lg"
                  onClick={handleConsultantClick}
                >
                  Falar com Especialista
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-lg font-semibold mb-4">Sua Nova Rotina</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm">9:00 - Todas as autorizações organizadas ✓</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm">10:30 - Comunicação com fornecedores ✓</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm">14:00 - Relatórios automáticos prontos ✓</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm">17:00 - Saindo tranquila, tudo organizado ✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Realidade Atual Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Sabemos Como É Sua Rotina Atual
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Como secretária médica, você é o centro operacional da clínica, nós sabemos como isso é 
              extremamente desafiador e, muitas vezes, subestimado.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center p-6 border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Sobrecarga de Trabalho</h3>
                <p className="text-sm text-gray-600">
                  Múltiplas tarefas urgentes ao mesmo tempo
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Comunicação Fragmentada</h3>
                <p className="text-sm text-gray-600">
                  WhatsApp, e-mail, telefone - tudo misturado
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Retrabalho Constante</h3>
                <p className="text-sm text-gray-600">
                  Refazer autorizações e confirmar informações
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Estresse e Ansiedade</h3>
                <p className="text-sm text-gray-600">
                  Medo de esquecer algo importante
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Transformação Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Sua Transformação Profissional Começa Aqui
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A Cirurgia Autorizada foi desenvolvida pensando especificamente em você, oferecendo ferramentas 
              que não apenas simplificam sua rotina, mas também valorizam seu papel essencial.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Controle Total em um Único Lugar</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Dashboard Unificado</h4>
                    <p className="text-gray-600">Todas as informações organizadas em uma interface intuitiva</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Visibilidade Completa</h4>
                    <p className="text-gray-600">Acompanhe o status de cada processo em tempo real</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Organização Automática</h4>
                    <p className="text-gray-600">Sistema organiza prioridades e alertas importantes</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:pl-8">
              <img 
                src="/images/status-autorizacoes.png" 
                alt="Dashboard para secretariado mostrando próximas cirurgias e autorizações" 
                className="rounded-lg shadow-lg w-full mb-4"
              />
              <p className="text-sm text-gray-500 text-center">
                Dashboard organizado especialmente para secretários médicos
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center p-8 border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Redução Drástica do Estresse</h3>
                <p className="text-gray-600">
                  Automação elimina tarefas repetitivas e frustrantes, liberando tempo para atividades de maior valor.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Comunicação Eficiente</h3>
                <p className="text-gray-600">
                  Mantenha o cirurgião informado sem interrupções, fortalecendo a parceria e aumentando a confiança.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 border-purple-200 bg-purple-50">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Valorização Profissional</h3>
                <p className="text-gray-600">
                  Transforme seu trabalho em um ativo estratégico, sendo reconhecida como gestora do fluxo cirúrgico.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Funcionalidades Específicas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ferramentas Pensadas Para Sua Rotina
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada funcionalidade foi desenvolvida para resolver problemas específicos do seu dia a dia.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <Target className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Gestão de Prioridades</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  O sistema identifica automaticamente o que é mais urgente e importante, 
                  ajudando você a organizar seu dia de forma inteligente.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Alertas automáticos de prazos</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Organização por urgência</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Lembretes personalizados</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <MessageSquare className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Comunicação Organizada</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Chega de WhatsApp e e-mails perdidos. Toda comunicação fica registrada e organizada 
                  por paciente e procedimento.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Histórico completo de conversas</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Notificações inteligentes</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Respostas automáticas</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                    <FileText className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Documentos Inteligentes</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Organização automática de todos os documentos, com busca rápida e acesso 
                  instantâneo quando você precisar.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Upload automático</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Busca por palavras-chave</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Backup automático</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="p-8">
              <CardContent className="pt-4">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <Shield className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Segurança e Tranquilidade</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  Todos os dados protegidos. 
                  Você pode trabalhar com total tranquilidade.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Criptografia avançada</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Conformidade LGPD</li>
                  <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" />Acesso controlado</li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Imagem demonstrativa da agenda */}
          <div className="mt-16 text-center">
            <img 
              src="/images/calendario.png" 
              alt="Agenda de cirurgias organizada para secretários médicos" 
              className="rounded-lg shadow-lg mx-auto max-w-4xl w-full"
            />
            <p className="text-sm text-gray-500 mt-4">
              Agenda inteligente que facilita o trabalho do secretariado médico
            </p>
          </div>
        </div>
      </section>

      {/* Depoimento Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-green-50 rounded-2xl p-8 md:p-12">
            <div className="flex justify-center mb-6">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
            <blockquote className="text-xl md:text-2xl text-gray-700 mb-6 italic">
              "O que falar da plataforma cirurgia autorizada? Posso chamar de melhor amiga? 
              A plataforma é indispensável no meu dia a dia, além de ser prática, didática e fácil, 
              ela me ajuda da melhor forma nas marcações e alinhamentos das cirurgias. 
              Já não vivo sem e indico de olhos fechados!"
            </blockquote>
            <div className="flex items-center justify-center">
              <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                <img 
                  src="/images/elissa.jpg" 
                  alt="Élissa Isidorio" 
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Élissa Isidorio</p>
                <p className="text-gray-600">Secretária Médica</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronta Para Transformar Sua Rotina?
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Descubra como a Cirurgia Autorizada pode simplificar seu trabalho e valorizar sua carreira.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg"
              onClick={() => window.open('https://calendly.com/cirurgiaautorizada-dev/30min', '_blank')}
            >
              Agendar Demonstração <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-green-700 hover:bg-gray-200 hover:text-green-700 px-8 py-4 text-lg"
              onClick={() => window.open('https://wa.me/5585982286045', '_blank')}
            >
              Conversar com Especialista
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Secretariado

